import { useEffect, useRef, useState, type ReactNode } from "react";
import { Check } from "lucide-react";
import OctermMark from "@/octerm-logo.svg";
import ClaudeMark from "@/logos/claude.svg";
import TeamsMark from "@/logos/microsoft-teams.svg";
import OpenAIMark from "@/logos/openai.svg";
import CopilotMark from "@/logos/microsoft-copilot.svg";
import { chamfer } from "@/components/brand/Press";
import { REVEAL_STYLES, Tile, useReveal } from "./Bento";

// How Octerm connects: data in from the operator's management software, answers out in their AI
// assistant and in Microsoft Teams. Each picture is drawn in the look of the app it stands for.
// The conversations and counts are sample data.

/** A product's logo as an app icon: the mark on a small white chamfered tile with a hairline edge. */
function LogoMark({ src, name }: { src: string; name: string }) {
  return (
    <span className="block bg-slate-200 p-px" style={{ clipPath: chamfer(4.4) }}>
      <span className="flex h-7 w-7 items-center justify-center bg-white" style={{ clipPath: chamfer(4) }}>
        <img src={src} alt={name} className="h-4 w-4" />
      </span>
    </span>
  );
}

/** The same frame for all three pictures, so they line up across the row: a bar along the top with the
 *  app's logo, as the app itself would show it, and what it shows under it. */
function Panel({ className = "", bar, children }: { className?: string; bar: ReactNode; children: ReactNode }) {
  return (
    <div className={`flex h-[240px] flex-col p-4 ${className}`}>
      <div className="flex h-[30px] items-center gap-2.5">{bar}</div>
      <div className="mt-3 flex flex-1 flex-col justify-center gap-3">{children}</div>
    </div>
  );
}

const SYNCED = [
  ["Units", "412"],
  ["Tenants", "371"],
  ["Rate changes", "14"],
  ["Move-ins", "6"],
] as const;

function SyncVisual() {
  return (
    <Panel
      className="border border-slate-100 bg-slate-50/60"
      bar={
        <>
          <LogoMark src={OctermMark} name="Octerm" />
          <span className="text-xs font-semibold text-slate-900">Synced to Octerm</span>
          <span className="ml-auto flex items-center gap-1 text-xs font-medium text-emerald-700">
            <Check className="h-3.5 w-3.5" aria-hidden />
            Up to date
          </span>
        </>
      }
    >
      <div className="divide-y divide-slate-100 border border-slate-200 bg-white">
        {SYNCED.map(([name, count]) => (
          <div key={name} className="flex items-center justify-between px-3 py-1.5 text-sm">
            <span className="text-slate-600">{name}</span>
            <span className="font-semibold tabular-nums text-slate-900">{count}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

/** The assistants Octerm's connector works in, each drawn in its own app's colours. */
const ASSISTANTS = [
  { name: "Claude", mark: ClaudeMark, panel: "bg-[#f7f5ef]", bubble: "bg-[#ebe7dc] text-[#1f1e1b]", text: "text-[#3a3935]" },
  { name: "ChatGPT", mark: OpenAIMark, panel: "bg-[#f9f9f9]", bubble: "bg-[#ececec] text-[#0d0d0d]", text: "text-[#2f2f2f]" },
  { name: "Copilot", mark: CopilotMark, panel: "bg-[#f4f6fb]", bubble: "bg-[#e3e9f6] text-[#1b1f2b]", text: "text-[#33384a]" },
] as const;

/** Where each logo sits in the deck, front (0) to back (2): the ones behind peek out to the right,
 *  smaller, their logos faded. */
const DECK = [
  "translateX(0) scale(1)",
  "translateX(8px) scale(0.88)",
  "translateX(16px) scale(0.76)",
];

/** The front logo is dealt to the back: lifted up and left, then tucked behind the others. Two copies
 *  of the keyframes, used in turn, so the animation restarts on every deal. */
const SHUFFLE_STYLES = [0, 1]
  .map(
    (n) => `
  @keyframes deal-${n} {
    0% { transform: ${DECK[0]}; z-index: 5; }
    40% { transform: translate(-10px, -12px) rotate(-10deg); z-index: 5; }
    55% { transform: translate(6px, -10px) rotate(6deg) scale(0.86); z-index: 0; }
    100% { transform: ${DECK[2]}; z-index: 0; }
  }`,
  )
  .join("") + `
  @keyframes name-in { from { transform: translateY(100%); opacity: 0; } to { transform: none; opacity: 1; } }
  @keyframes name-out { from { transform: none; opacity: 1; } to { transform: translateY(-100%); opacity: 0; } }
  @media (prefers-reduced-motion) { .deal, .name-in, .name-out { animation: none !important; } }
`;

/** The same question in each assistant, one after another: the logos along the top are dealt like a
 *  deck, the front one's name beside it, and the chat takes that app's colours. */
function AssistantVisual() {
  const [deals, setDeals] = useState(0);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = setInterval(() => setDeals((d) => d + 1), 3000);
    return () => clearInterval(timer);
  }, []);
  const n = ASSISTANTS.length;
  const front = deals % n;
  const a = ASSISTANTS[front]!;
  // The logo dealt to the back this time: the one that was in front.
  const dealt = deals > 0 ? (front + n - 1) % n : -1;
  return (
    <Panel
      className={`rounded-lg transition-colors duration-700 ${a.panel}`}
      bar={
        <>
          <style>{SHUFFLE_STYLES}</style>
          <span className="relative h-[30px] w-[46px] shrink-0">
            {ASSISTANTS.map((x, i) => {
              const rank = (i - front + n) % n;
              return (
                <span
                  key={x.name}
                  className={`absolute left-0 top-0 origin-right transition-transform duration-500 ease-out [&_img]:transition-[opacity,filter] [&_img]:duration-500 ${rank > 0 ? "[&_img]:opacity-35 [&_img]:grayscale" : ""} ${i === dealt ? "deal" : ""}`}
                  style={{
                    transform: DECK[rank],
                    zIndex: n - rank,
                    animation: i === dealt ? `deal-${deals % 2} 700ms cubic-bezier(0.2, 0.8, 0.2, 1) both` : undefined,
                  }}
                >
                  <LogoMark src={x.mark} name={rank === 0 ? x.name : ""} />
                </span>
              );
            })}
          </span>
          {/* The names roll like a counter: the last one up and out, the new one up from below. */}
          <span className="relative h-4 flex-1 overflow-hidden text-xs font-semibold leading-4 text-slate-900">
            {deals > 0 && (
              <span key={`out-${deals}`} className="name-out absolute inset-0" style={{ animation: `name-out 400ms cubic-bezier(0.4, 0, 0.6, 1) both` }}>
                {ASSISTANTS[(front + n - 1) % n]!.name}
              </span>
            )}
            <span key={`in-${deals}`} className="name-in absolute inset-0" style={{ animation: deals ? "name-in 450ms 120ms cubic-bezier(0.2, 0.8, 0.2, 1) both" : undefined }}>
              {a.name}
            </span>
          </span>
        </>
      }
    >
      <p className={`ml-auto max-w-[85%] rounded-xl px-3 py-2 text-sm transition-colors duration-700 ${a.bubble}`}>Which facilities are underpriced?</p>
      <div className="flex items-start gap-2.5 pr-2">
        <img key={a.name} src={a.mark} alt="" className="name-in mt-0.5 h-4 w-4 shrink-0" style={{ animation: deals ? "name-in 450ms 150ms cubic-bezier(0.2, 0.8, 0.2, 1) both" : undefined }} />
        <p className={`text-sm leading-relaxed transition-colors duration-700 ${a.text}`}>Boise, Round Rock, and Mesa. Boise could go up $15 to $179.</p>
      </div>
    </Panel>
  );
}

function TeamsMessage({ avatar, name, time, children }: { avatar: ReactNode; name: string; time: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5">
      {avatar}
      <div className="min-w-0 flex-1 rounded-md bg-white px-3 py-1.5 shadow-[0_1px_2px_rgba(0,0,0,0.08)]">
        <p className="text-[11px] text-slate-500">
          <span className="font-semibold text-slate-800">{name}</span> {time}
        </p>
        <p className="mt-0.5 text-[13px] leading-snug text-slate-800">{children}</p>
      </div>
    </div>
  );
}

function TeamsVisual() {
  return (
    <Panel
      className="rounded-lg bg-[#f3f3f6]"
      bar={
        <>
          <LogoMark src={TeamsMark} name="Microsoft Teams" />
          <span className="text-xs font-semibold text-slate-900">Lakeside facility</span>
        </>
      }
    >
      <TeamsMessage
        name="Dana Price"
        time="9:41 AM"
        avatar={<span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#d6d9f5] text-[10px] font-semibold text-[#4f52b2]">DP</span>}
      >
        <span className="rounded-sm bg-[#e8ebfa] px-1 font-medium text-[#4f52b2]">Octerm</span> move-ins since the rate change?
      </TeamsMessage>
      <TeamsMessage
        name="Octerm"
        time="9:41 AM"
        avatar={
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-logo-background">
            <img src={OctermMark} alt="" className="h-4 w-4" />
          </span>
        }
      >
        Steady at 8.1 a week before and 7.8 since.
      </TeamsMessage>
    </Panel>
  );
}

export function Connectors() {
  const ref = useRef<HTMLDivElement>(null);
  useReveal(ref);
  return (
    <section id="connectors" className="scroll-mt-16 bg-white py-24">
      <style>{REVEAL_STYLES}</style>
      <div ref={ref} className="mx-auto max-w-6xl px-5">
        <h2 className="max-w-2xl text-balance text-[clamp(32px,5vw,48px)] font-semibold leading-[1.05] tracking-[-0.03em] text-logo">
          Plugs into what you already use.
        </h2>
        <div className="mt-12 grid gap-4 lg:grid-cols-3">
          <Tile index={0} title="Synced with your software." desc="Nothing to export or enter.">
            <SyncVisual />
          </Tile>
          <Tile index={1} title="Ask your AI about your markets." desc="Any assistant that supports MCP.">
            <AssistantVisual />
          </Tile>
          <Tile index={2} title="@Octerm in any facility chat." desc="Answers where your team already talks.">
            <TeamsVisual />
          </Tile>
        </div>
      </div>
    </section>
  );
}
